// src/redux/chatSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import UserRoleService from "../../services/api/userRoleService";
import { setSessionExpired, setTotalPage } from "./globalSlice";
import { setIsActivatePopup, setIsEditpopup } from "./popUpSlice";
import { unauthorizedError } from "../../utils/helpers";

const userRole = new UserRoleService();

export const getRolesData = createAsyncThunk(
  `/admin/role`,
  async (
    { page, limit, searchQuery, sortKey, sortDirection, navigate },
    { dispatch }
  ) => {
    try {
      const response = await userRole.getRolesData({
        page,
        limit,
        searchQuery,
        sortKey,
        sortDirection,
        navigate,
      });
      dispatch(setTotalPage(response?.total));
      return response;
    } catch (error) {
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
      }
      throw error;
    }
  }
);

//add new role api integration
export const addNewRoleData = createAsyncThunk(
  "/admin/addRole/",
  async (
    { body, page, limit, searchQuery, sortKey, sortDirection, navigate },
    { dispatch }
  ) => {
    try {
      let searchTerm = searchQuery || "";
      const response = await userRole.addNewRole(body, navigate);
      dispatch(
        getRolesData({
          page,
          limit,
          searchQuery: searchTerm,
          sortKey,
          sortDirection,
          navigate,
        })
      );
      toast.success("New role added successfully");
      dispatch(setInvalidPermissions({}));
      dispatch(setIsActivatePopup(false));
      dispatch(setIsEditpopup(false));
      return response;
    } catch (error) {
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        dispatch(setInvalidPermissions(error?.data?.invalidPermissions));
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message
        );
      }

      throw error?.data?.message;
    }
  }
);

export const editRoleData = createAsyncThunk(
  "/admin/editRole/",
  async (
    { body, id, page, limit, searchQuery, sortKey, sortDirection, navigate },
    { dispatch }
  ) => {
    try {
      const response = await userRole.editRole({
        body,
        id,
        page,
        limit,
        navigate,
      });
      dispatch(
        getRolesData({
          page,
          limit,
          searchQuery,
          sortKey,
          sortDirection,
          navigate,
        })
      );
      toast.success(response.message);
      dispatch(setInvalidPermissions({}));
      dispatch(setIsActivatePopup(false));
      dispatch(setIsEditpopup(false));
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        dispatch(setInvalidPermissions(error?.data?.invalidPermissions));
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message
        );
      }
      throw error?.data?.message;
    }
  }
);

export const getRoleUsersById = createAsyncThunk(
  "admin/role/users/",
  async ({ id, navigate }, { dispatch }) => {
    try {
      const response = await userRole.getRoleUsersById(id, navigate);
      return response;
    } catch (error) {
      toast.error(error?.data?.error);
      throw error;
    }
  }
);

export const deleteRolesData = createAsyncThunk(
  "/admin/deleteRoles/",
  async (
    { body, page, limit, searchQuery, sortKey, sortDirection, navigate },
    { dispatch }
  ) => {
    try {
      const response = await userRole.deleteRoles({ body });
      dispatch(
        getRolesData({
          page,
          limit,
          searchQuery,
          sortKey,
          sortDirection,
          navigate,
        })
      );

      const plural = body.roleIds.length > 1 ? "s" : "";
      toast.success(`Role${plural} deleted successfully`);
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message
        );
      }
      throw error?.data?.message;
    }
  }
);

const editRoleSlice = createSlice({
  name: "editRoles",
  initialState: {
    loading: false,
    roles: [],
    selectedRoleData: null,
    roleUsers: null,
    renderallCustomData: null,
    page: 1,
    limit: 10,
    searchItem: "",
    addUserRoles: {
      role: "",
      permissions: null,
    },
    invalidPermissions: {},
    sortConfig: { key: "name", direction: "asc" },
  },
  reducers: {
    setSelectedRoleData: (state, action) => {
      state.selectedRoleData = action.payload;
    },
    clearedSelectedRole: (state, action) => {
      state.selectedRoleData = null;
    },
    setRoleUsers: (state, action) => {
      state.roleUsers = action.payload;
    },
    clearRoleUsers: (state) => {
      state.roleUsers = null;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setLimit: (state, action) => {
      state.limit = action.payload;
    },
    setSearchItem: (state, action) => {
      state.searchItem = action.payload;
    },
    setSortConfig: (state, action) => {
      state.sortConfig = action.payload;
    },
    setAddRoleData: (state, action) => {
      state.addUserRoles.role = action.payload?.role;
      state.addUserRoles.permissions = action.payload?.permissions;
    },
    setInvalidPermissions: (state, action) => {
      state.invalidPermissions = action.payload ?? {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRolesData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getRolesData.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = action.payload;
        state.renderallCustomData = action.payload;
      })
      .addCase(getRolesData.rejected, (state) => {
        state.loading = false;
      })
      .addCase(addNewRoleData.pending, (state) => {
        state.loading = true;
      })
      .addCase(addNewRoleData.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addNewRoleData.rejected, (state) => {
        state.loading = false;
      })
      .addCase(editRoleData.pending, (state) => {
        state.loading = true;
      })
      .addCase(editRoleData.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(editRoleData.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getRoleUsersById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getRoleUsersById.fulfilled, (state, action) => {
        state.loading = false;
        state.roleUsers = action.payload|| null;
      })
      .addCase(getRoleUsersById.rejected, (state) => {
        state.loading = false;
      })
      .addCase(deleteRolesData.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteRolesData.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteRolesData.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  setSelectedRoleData,
  clearedSelectedRole,
  setPage,
  setLimit,
  setSearchItem,
  setSortConfig,
  setAddRoleData,
  setInvalidPermissions,
  setRoleUsers,
  clearRoleUsers,
} = editRoleSlice.actions;

export default editRoleSlice.reducer;
